import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useState, useCallback, useEffect } from 'react';
import { useOpenState } from '../hooks';
import { isShallowEqual } from "../utils";
export var useChipValues = function useChipValues(initialValue, allowDuplicates, onChipAdded, onChipAlreadyAdded, onChipRemoved, delimiter) {
  var _useState = useState(initialValue),
      _useState2 = _slicedToArray(_useState, 2),
      values = _useState2[0],
      setValues = _useState2[1];

  useEffect(function () {
    if (isShallowEqual(values, initialValue)) return;
    setValues(initialValue); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);
  var addChip = useCallback(function (chipValueString) {
    if (!chipValueString) return; // Split input if delimiter is given

    var newChips = (delimiter ? chipValueString.split(delimiter) : [chipValueString]).filter(function (x) {
      return !!x;
    }); // if no chips, return

    if (newChips.length === 0) return;
    setValues(function (oldChips) {
      var newValues = [].concat(_toConsumableArray(oldChips), _toConsumableArray(newChips)); // dedupe and alert

      if (!allowDuplicates) {
        newValues = _toConsumableArray(new Set(newValues));
        var alreadyAddedIndexes = newChips.map(function (chipValue) {
          return values.indexOf(chipValue);
        }).filter(function (x) {
          return x >= 0;
        }); // send the index array to handle bouncing effect

        if (alreadyAddedIndexes.length > 0) onChipAlreadyAdded(alreadyAddedIndexes);
      }

      if (newValues.length > 0) onChipAdded(newValues);
      return newValues;
    });
  }, [values, delimiter, allowDuplicates, onChipAdded, onChipAlreadyAdded]);
  var removeChip = useCallback(function (chipValue) {
    var index = values.indexOf(chipValue);

    if (index > -1) {
      var newValues = values.filter(function (v, i) {
        return i !== index;
      });
      setValues(newValues);
      onChipRemoved(newValues);
    }
  }, [onChipRemoved, values]);
  return {
    values: values,
    addChip: addChip,
    removeChip: removeChip
  };
};
export var useAutocompleteStates = function useAutocompleteStates() {
  var optionList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var inputValue = arguments.length > 1 ? arguments[1] : undefined;
  var initialOption = inputValue && optionList.length ? 0 : -1;

  var _useState3 = useState(initialOption),
      _useState4 = _slicedToArray(_useState3, 2),
      focusedOption = _useState4[0],
      setFocusedOption = _useState4[1];

  var _useOpenState = useOpenState(true),
      _useOpenState2 = _slicedToArray(_useOpenState, 4),
      show = _useOpenState2[0],
      hide = _useOpenState2[1],
      setHidden = _useOpenState2[2],
      hidden = _useOpenState2[3];

  useEffect(function () {
    setFocusedOption(initialOption);
  }, [initialOption]);
  var showAutocomplete = useCallback(function () {
    setHidden(false);
    setFocusedOption(initialOption);
  }, [setHidden, setFocusedOption, initialOption]);
  var hideAutocomplete = useCallback(function () {
    setHidden(true);
    setFocusedOption(-1);
  }, [setHidden, setFocusedOption]);
  var moveDownAutocomplete = useCallback(function () {
    if (optionList.length > 0) {
      setFocusedOption((focusedOption + 1) % optionList.length);
    }
  }, [setFocusedOption, optionList, focusedOption]);
  var moveUpAutocomplete = useCallback(function () {
    if (optionList.length > 0) {
      if (focusedOption <= 0) {
        setFocusedOption(optionList.length - 1);
      } else {
        setFocusedOption(focusedOption - 1);
      }
    }
  }, [setFocusedOption, optionList, focusedOption]);
  return {
    hidden: hidden,
    focusedOption: focusedOption,
    showAutocomplete: showAutocomplete,
    hideAutocomplete: hideAutocomplete,
    moveDownAutocomplete: moveDownAutocomplete,
    moveUpAutocomplete: moveUpAutocomplete
  };
};